import { useEffect, useRef, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { confermaSpedizioneBulk, riepilogoConfermaBulk } from "../confermaBulk";
import { nomeCanaleDiretto } from "../confermaCanaleApi";
import type { Order } from "../types";

interface SpedizioneBulkConfirmDialogProps {
  /** Quanti ordini sono selezionati. */
  quanti: number;
  /** I marketplace raggiungibili direttamente, in chiaro (es. "Amazon"). */
  canali: string[];
  /**
   * Quanti ordini NON hanno un canale raggiungibile: dal 2026-09-01 sono solo
   * gli ordini senza canale, perche' tutti e sei i marketplace parlano diretti
   * — WooCommerce compreso, che fino a quel giorno finiva qui dentro per un
   * difetto del filtro, non per una scelta.
   */
  quantiViaPoleepo: number;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const TITLE_ID = "conferma-spedizione-bulk-title";

/**
 * Conferma «Spedito» su N ordini insieme.
 *
 * Perché un dialog qui e non sul bottone del singolo ordine: cambia la scala,
 * non il gesto. Questo comando manda la mail di spedizione a N clienti VERI in
 * un colpo, e la selezione nella lista è facile da sbagliare — un filtro
 * lasciato aperto e "seleziona tutto" fanno partire decine di mail. Su un
 * ordine alla volta l'errore si vede e si rimedia; a lotti no.
 *
 * Stesso pattern modale del resto (role=dialog, overlay, Esc, Annulla/Conferma):
 * l'esecuzione e il feedback li fa il parent, che smonta il dialog alla scelta.
 */
export function SpedizioneBulkConfirmDialog({
  quanti,
  canali,
  quantiViaPoleepo,
  busy,
  onConfirm,
  onCancel,
}: SpedizioneBulkConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    cancelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      data-testid="conferma-spedizione-bulk-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-line bg-card shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id={TITLE_ID} className="font-serif text-[17px] font-medium text-ink">
            Dichiarare spedit{quanti === 1 ? "o" : "i"} {quanti} ordin{quanti === 1 ? "e" : "i"}?
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-3">
            Ogni cliente riceve la mail di spedizione dal marketplace.
          </p>
        </header>

        <div className="px-5 py-4 text-[12.5px] leading-relaxed text-ink">
          {canali.length > 0 && (
            <>
              <p>
                Su <span className="font-medium">{canali.join(", ")}</span> il tracking e il
                corriere vanno <span className="font-medium">direttamente al marketplace</span>,
                presi dall&apos;ordine.
              </p>
              <p className="mt-1.5 text-ink-3">
                Lì gli ordini già spediti vengono saltati e quelli annullati rifiutati: non serve
                ripulire la selezione a mano.
              </p>
            </>
          )}
          {quantiViaPoleepo > 0 && (
            <p className={canali.length > 0 ? "mt-3" : ""}>
              Gli altri <span className="font-medium">{quantiViaPoleepo}</span> non hanno un canale
              raggiungibile da qui: per loro viene aggiornato lo{" "}
              <span className="font-medium">stato su Poleepo</span>, che è l&apos;unica strada
              disponibile —{" "}
              <span className="text-ink-3">
                senza controlli sullo stato dell&apos;ordine e senza comunicare il tracking
              </span>
              .
            </p>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <button
            ref={cancelRef}
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-sm border border-line bg-card px-3 py-1.5 text-[12.5px] font-medium text-ink-2 hover:border-accent disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            data-testid="conferma-spedizione-bulk-ok"
            disabled={busy}
            onClick={onConfirm}
            className="rounded-sm border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {busy ? "Comunico…" : "Sì, spedisci"}
          </button>
        </footer>
      </div>
    </div>
  );
}

/** Il dialog collegato alla conferma vera: canali in chiaro, invio, esito.
 *  ``onFine`` riceve il riepilogo (o l'errore) a invio concluso. */
export function ConfermaSpedizioneBulk({
  ids,
  orders,
  onFine,
  onAnnulla,
}: {
  ids: string[];
  orders: Order[];
  onFine: (esito: { tono: string; testo: string }) => void;
  onAnnulla: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const scelti = orders.filter((o) => ids.includes(o.id));
  return (
    <SpedizioneBulkConfirmDialog
      quanti={ids.length}
      canali={[...new Set(scelti.map((o) => nomeCanaleDiretto(o) ?? "").filter(Boolean))]}
      quantiViaPoleepo={scelti.filter((o) => !nomeCanaleDiretto(o)).length}
      busy={busy}
      onCancel={() => {
        if (!busy) onAnnulla();
      }}
      onConfirm={() => {
        setBusy(true);
        void (async () => {
          try {
            onFine(riepilogoConfermaBulk(await confermaSpedizioneBulk(ids)));
          } catch (err) {
            onFine({ tono: "errore", testo: prettifyApiError(err) });
          } finally {
            setBusy(false);
          }
        })();
      }}
    />
  );
}
